<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Incidents</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">CRA Article 14 — Track and report security incidents to ENISA</p>
      </div>
      <button @click="openCreate" class="btn-primary">Add Incident</button>
    </div>

    <DataTable
      :columns="columns"
      resourceName="incidents"
      @rowClick="openEdit"
    >
      <template #cell="{ column, value }">
        <template v-if="column.key === 'severity'">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            :class="{
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': value === 'CRITICAL',
              'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200': value === 'HIGH',
              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': value === 'MEDIUM',
              'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': value === 'LOW'
            }">{{ value }}</span>
        </template>
        <template v-else-if="column.key === 'status'">
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
            :class="statusClass(value)">{{ statusLabel(value) }}</span>
        </template>
        <template v-else-if="column.key === 'is_public'">
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="value ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'">
            {{ value ? 'Public' : 'Private' }}
          </span>
        </template>
        <template v-else-if="column.key === 'type'">
          <span class="text-xs text-gray-600 dark:text-gray-400">{{ value === 'severe_incident' ? 'Severe Incident' : 'Exploited Vuln' }}</span>
        </template>
      </template>
    </DataTable>

    <!-- Add/Edit Incident Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" @click.self="closeModal">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-start mb-6">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ editing ? 'Edit Incident' : 'Create Incident' }}
            </h2>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <!-- Public/Private toggle (auto-saves when editing) -->
          <div class="mb-6 p-4 rounded-lg border-2" :class="form.is_public ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700/50'">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-sm font-semibold" :class="form.is_public ? 'text-green-800 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'">
                  {{ form.is_public ? 'Public — visible on trust center' : 'Private — internal only' }}
                </span>
                <p class="text-xs mt-0.5" :class="form.is_public ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'">
                  {{ form.is_public ? 'This incident will appear on the public portal' : 'Only visible to admin panel users' }}
                </p>
              </div>
              <button type="button" @click="editing ? toggleVisibility() : (form.is_public = !form.is_public)"
                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                :class="form.is_public ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'">
                <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                  :class="form.is_public ? 'translate-x-6' : 'translate-x-1'"></span>
              </button>
            </div>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
              <input v-model="form.title" required class="input mt-1" />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                <select v-model="form.type" required class="input mt-1">
                  <option value="actively_exploited_vulnerability">Actively Exploited Vulnerability</option>
                  <option value="severe_incident">Severe Incident</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Severity</label>
                <select v-model="form.severity" required class="input mt-1">
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Product</label>
                <select v-model="form.product_id" @change="onProductChange" class="input mt-1">
                  <option :value="null">None</option>
                  <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Discovered At</label>
                <input v-model="form.discovered_at" type="datetime-local" required class="input mt-1" />
              </div>
            </div>

            <div v-if="form.product_id && availableVersions.length > 0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Affected Versions</label>
              <div class="mt-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 max-h-40 overflow-y-auto">
                <input
                  v-if="availableVersions.length > 5"
                  v-model="versionSearch"
                  type="text"
                  placeholder="Search versions..."
                  class="w-full px-3 py-1.5 text-sm border-b border-gray-200 dark:border-gray-600 bg-transparent dark:text-white focus:outline-none"
                />
                <label
                  v-for="v in filteredVersions" :key="v.id"
                  class="flex items-center px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                >
                  <input type="checkbox" :value="v.id" v-model="form.affected_version_ids"
                    class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600" />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">{{ v.version }}</span>
                </label>
                <p v-if="filteredVersions.length === 0" class="px-3 py-2 text-xs text-gray-400">No matching versions</p>
              </div>
              <div class="flex gap-2 mt-1">
                <button type="button" @click="form.affected_version_ids = availableVersions.map(v => v.id)" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">Select all</button>
                <button type="button" @click="form.affected_version_ids = []" class="text-xs text-gray-500 dark:text-gray-400 hover:underline">Clear</button>
              </div>
            </div>

            <div v-if="isExploitedVuln">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Linked Vulnerability</label>
              <select v-model="form.vulnerability_id" class="input mt-1">
                <option :value="null">None</option>
                <option v-for="v in vulnerabilities" :key="v.id" :value="v.id">{{ v.cve_id || v.synopsis }} ({{ v.severity }})</option>
              </select>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Link this incident to an existing vulnerability for ENISA reporting</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <RichTextEditor v-model="form.description" placeholder="Describe the incident..." />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Impact Assessment</label>
              <textarea v-model="form.impact_assessment" rows="2" class="input mt-1"></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mitigation Steps</label>
              <textarea v-model="form.mitigation_steps" rows="2" class="input mt-1"></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Root Cause</label>
              <textarea v-model="form.root_cause" rows="2" class="input mt-1"></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Affected EU Member States</label>
              <input v-model="form.affected_member_states" placeholder="e.g. DE, FR, NL" class="input mt-1" />
            </div>

            <div v-if="form.is_public">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Public Summary <span class="text-gray-400 font-normal">(shown on trust center)</span></label>
              <textarea v-model="form.public_summary" rows="2" class="input mt-1" placeholder="Sanitized summary for external visibility"></textarea>
            </div>

            <!-- Timeline (read-only when editing) -->
            <div v-if="editing && selected" class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">ENISA Reporting Timeline</h3>
              <div class="space-y-2">
                <div class="flex items-center justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Early Warning (24h)</span>
                  <span :class="deadlineClass(selected.early_warning_due, selected.early_warning_sent_at)">
                    {{ selected.early_warning_sent_at ? 'Sent' : formatDeadline(selected.early_warning_due) }}
                  </span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Full Notification (72h)</span>
                  <span :class="deadlineClass(selected.full_notification_due, selected.full_notification_sent_at)">
                    {{ selected.full_notification_sent_at ? 'Sent' : formatDeadline(selected.full_notification_due) }}
                  </span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Final Report ({{ selected.type === 'severe_incident' ? '30d' : '14d' }})</span>
                  <span :class="deadlineClass(selected.final_report_due, selected.final_report_sent_at)">
                    {{ selected.final_report_sent_at ? 'Sent' : formatDeadline(selected.final_report_due) }}
                  </span>
                </div>
              </div>

              <!-- Status advancement -->
              <div class="mt-4 flex gap-2 flex-wrap">
                <select v-model="form.status" class="input text-sm">
                  <option value="draft">Draft</option>
                  <option value="early_warning_sent">Early Warning Sent</option>
                  <option value="full_notification_sent">Full Notification Sent</option>
                  <option value="final_report_sent">Final Report Sent</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button v-if="editing" type="button" @click="confirmDelete" class="px-4 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20" :disabled="saving">Delete</button>
              <button type="button" @click="closeModal" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700" :disabled="saving">Cancel</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                {{ saving ? 'Saving...' : (editing ? 'Save Changes' : 'Create Incident') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Delete Incident"
      message="Are you sure you want to delete this incident? This action cannot be undone."
      :loading="deleting"
      @confirm="handleDelete"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import DataTable from '@/components/DataTable.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'IncidentsView',
  components: { DataTable, DeleteConfirmationModal, RichTextEditor },
  setup() {
    const store = useStore()
    const showModal = ref(false)
    const showDeleteModal = ref(false)
    const editing = ref(false)
    const selected = ref(null)
    const saving = ref(false)
    const deleting = ref(false)
    const products = ref([])
    const vulnerabilities = ref([])
    const availableVersions = ref([])
    const versionSearch = ref('')

    const filteredVersions = computed(() => {
      if (!versionSearch.value) return availableVersions.value
      const q = versionSearch.value.toLowerCase()
      return availableVersions.value.filter(v => v.version.toLowerCase().includes(q))
    })

    const fetchVersionsForProduct = async (productId) => {
      if (!productId) { availableVersions.value = []; return }
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId()}/products/${productId}/versions`, {
          headers: headers(), params: { per_page: 500 }
        })
        availableVersions.value = res.data?.data || res.data || []
      } catch (e) {
        console.error('Failed to fetch versions:', e)
        availableVersions.value = []
      }
    }

    const onProductChange = () => {
      form.value.affected_version_ids = []
      versionSearch.value = ''
      fetchVersionsForProduct(form.value.product_id)
    }

    const emptyForm = () => ({
      title: '', description: '', type: 'actively_exploited_vulnerability', severity: 'HIGH',
      status: 'draft', product_id: null, vulnerability_id: null,
      discovered_at: new Date().toISOString().slice(0, 16),
      impact_assessment: '', mitigation_steps: '', root_cause: '', affected_member_states: '',
      is_public: false, public_summary: '', affected_version_ids: []
    })

    const isExploitedVuln = computed(() => form.value.type === 'actively_exploited_vulnerability')
    const form = ref(emptyForm())

    const columns = [
      { key: 'title', label: 'Title', sortable: true },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'severity', label: 'Severity', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'is_public', label: 'Visibility', sortable: false }
    ]

    const headers = () => ({ Authorization: `Bearer ${store.state.token}` })
    const tenantId = () => store.state.currentTenantId

    const fetchProducts = async () => {
      try {
        if (!tenantId()) return
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId()}/products`, { headers: headers(), params: { per_page: 100 } })
        products.value = res.data?.data || res.data || []
      } catch (e) { console.error('Failed to fetch products:', e) }
    }

    const fetchVulnerabilities = async () => {
      try {
        if (!tenantId()) return
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId()}/vulns`, { headers: headers(), params: { per_page: 200 } })
        vulnerabilities.value = res.data?.data || res.data || []
      } catch (e) { console.error('Failed to fetch vulnerabilities:', e) }
    }

    const openCreate = () => { editing.value = false; selected.value = null; form.value = emptyForm(); showModal.value = true }

    const openEdit = (incident) => {
      editing.value = true
      selected.value = incident
      form.value = {
        title: incident.title, description: incident.description, type: incident.type,
        severity: incident.severity, status: incident.status, product_id: incident.product_id || null,
        vulnerability_id: incident.vulnerability_id || null,
        discovered_at: incident.discovered_at ? incident.discovered_at.slice(0, 16) : '',
        impact_assessment: incident.impact_assessment || '', mitigation_steps: incident.mitigation_steps || '',
        root_cause: incident.root_cause || '', affected_member_states: incident.affected_member_states || '',
        is_public: incident.is_public || false, public_summary: incident.public_summary || '',
        affected_version_ids: (incident.affected_versions || []).map(v => v.id)
      }
      versionSearch.value = ''
      if (incident.product_id) fetchVersionsForProduct(incident.product_id)
      showModal.value = true
    }

    const toggleVisibility = async () => {
      if (!editing.value || !selected.value) return
      form.value.is_public = !form.value.is_public
      try {
        await store.dispatch('incidents/updateData', {
          tenantId: tenantId(), id: selected.value.id,
          data: { is_public: form.value.is_public }
        })
        selected.value.is_public = form.value.is_public
        store.dispatch('notification/showNotification', { type: 'success', message: form.value.is_public ? 'Now public' : 'Now private', duration: 2000 })
      } catch (e) {
        form.value.is_public = !form.value.is_public
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to update visibility' })
      }
    }

    const closeModal = () => { showModal.value = false; selected.value = null; editing.value = false; form.value = emptyForm(); availableVersions.value = []; versionSearch.value = '' }

    const refreshData = () => store.dispatch('incidents/fetchData', {
      tenantId: tenantId(), page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'desc'
    })

    const handleSubmit = async () => {
      saving.value = true
      try {
        const data = { ...form.value }
        if (data.discovered_at) data.discovered_at = new Date(data.discovered_at).toISOString()
        if (!data.product_id) delete data.product_id
        if (!data.vulnerability_id) delete data.vulnerability_id
        if (!data.affected_version_ids || data.affected_version_ids.length === 0) delete data.affected_version_ids

        if (editing.value) {
          await store.dispatch('incidents/updateData', { tenantId: tenantId(), id: selected.value.id, data })
        } else {
          await store.dispatch('incidents/createData', { tenantId: tenantId(), data })
        }
        await refreshData()
        store.dispatch('notification/showNotification', { type: 'success', message: `Incident ${editing.value ? 'updated' : 'created'} successfully` })
        closeModal()
      } catch (e) {
        console.error('Error saving incident:', e)
        store.dispatch('notification/showNotification', { type: 'error', message: e.response?.data?.error || 'Failed to save incident' })
      } finally { saving.value = false }
    }

    const confirmDelete = () => { showDeleteModal.value = true }
    const handleDelete = async () => {
      deleting.value = true
      try {
        await store.dispatch('incidents/deleteData', { tenantId: tenantId(), id: selected.value.id })
        await refreshData()
        store.dispatch('notification/showNotification', { type: 'success', message: 'Incident deleted' })
        closeModal()
        showDeleteModal.value = false
      } catch (e) {
        store.dispatch('notification/showNotification', { type: 'error', message: 'Failed to delete incident' })
      } finally { deleting.value = false }
    }

    const statusClass = (s) => ({
      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300': s === 'draft',
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': s === 'early_warning_sent',
      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': s === 'full_notification_sent',
      'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300': s === 'final_report_sent',
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': s === 'closed',
    })
    const statusLabel = (s) => ({ draft: 'Draft', early_warning_sent: 'Early Warning', full_notification_sent: 'Full Notification', final_report_sent: 'Final Report', closed: 'Closed' }[s] || s)

    const formatDeadline = (d) => {
      if (!d) return '—'
      const dt = new Date(d)
      const now = new Date()
      const diff = dt - now
      if (diff < 0) return 'Overdue'
      const hours = Math.floor(diff / 3600000)
      if (hours < 48) return `${hours}h remaining`
      return `${Math.floor(hours / 24)}d remaining`
    }

    const deadlineClass = (due, sent) => {
      if (sent) return 'text-green-600 dark:text-green-400 font-medium'
      if (!due) return ''
      const diff = new Date(due) - new Date()
      if (diff < 0) return 'text-red-600 dark:text-red-400 font-semibold'
      if (diff < 12 * 3600000) return 'text-orange-600 dark:text-orange-400 font-medium'
      return 'text-gray-600 dark:text-gray-400'
    }

    onMounted(() => { fetchProducts(); fetchVulnerabilities() })

    return {
      columns, showModal, showDeleteModal, editing, selected, form, saving, deleting, products, vulnerabilities,
      isExploitedVuln, openCreate, openEdit, closeModal, handleSubmit, confirmDelete, handleDelete,
      toggleVisibility, statusClass, statusLabel, formatDeadline, deadlineClass,
      availableVersions, versionSearch, filteredVersions, onProductChange
    }
  }
}
</script>
